import { faker } from '@faker-js/faker';
import { http, HttpResponse } from 'msw';

import { env } from '@/config/env';
import { axios } from '@/lib/axios';
import { networkDelay } from '@/test/mocks/utils';

import { GetTopicAssessmentSummary } from '../types';

export const getTopicAssessmentSummary = (
  topicId: string,
  assessmentId: string
): Promise<GetTopicAssessmentSummary.Root> => {
  return axios.get(
    '/assessment/v1/users/reports/get-topic-assessment-summary',
    {
      params: {
        topicId,
        assessmentId,
      },
    }
  );
};

const generateTopicAssessmentAttempt = (
  attemptNumber: number
): GetTopicAssessmentSummary.Attempt => {
  const totalQuestions = faker.number.int({ min: 10, max: 20 });
  const correctQuestions = faker.number.int({ min: 0, max: totalQuestions });
  const accuracy = Math.round((correctQuestions / totalQuestions) * 100);

  return {
    attemptNumber,
    date: faker.date.recent({ days: 30 }).getTime().toString().slice(0, -3),
    marksObtained: faker.number.int({ min: 0, max: 100 }),
    coinsEarned: faker.number.int({ min: 0, max: 10 }),
    accuracy,
    proficiency: faker.number.float({ min: 0, max: 1, fractionDigits: 3 }),
    speedPercentile: faker.number.float({
      min: 0,
      max: 100,
      fractionDigits: 4,
    }),
    timeSpentSec: faker.number.int({ min: 30, max: 600 }),
    totalQuestions,
    correctQuestions,
    testType: 'ASSESSMENT',
    sessionId: faker.string.uuid(),
  };
};

const generateAssessmentAttemptReport = (
  topicId: string
): GetTopicAssessmentSummary.AssessmentAttemptReport => {
  const numberOfAttempts = faker.number.int({ min: 1, max: 4 });
  const attempts = Array.from({ length: numberOfAttempts }, (_, index) =>
    generateTopicAssessmentAttempt(index + 1)
  );

  return {
    assessmentId: faker.string.uuid(),
    assessmentType: faker.helpers.arrayElement(['WORKSHEET', 'EXAM', 'QUIZ']),
    moduleId: faker.string.uuid(),
    topicId,
    testType: 'ASSESSMENT',
    roomId: null,
    assessmentName: faker.helpers.arrayElement([
      'Welcome to ML',
      'Welcome to AI',
      'Introduction to Data Science',
      'Fundamentals of Programming',
    ]),
    assessmentImageUrl: 'https://picsum.photos/200',
    banner: 'https://picsum.photos/200',
    roomName: null,
    roomType: null,
    preDefinedRoomId: null,
    reAttemptAllowed: faker.datatype.boolean(),
    viewType: faker.helpers.arrayElement(['POST', 'PRE']),
    attempts,
  };
};

const generateTopicAssessmentSummary = (
  topicId: string
): GetTopicAssessmentSummary.Root => {
  const numberOfReports = faker.number.int({ min: 1, max: 3 });
  const assessmentAttemptReports = Array.from({ length: numberOfReports }, () =>
    generateAssessmentAttemptReport(topicId)
  );

  const totalAttempts = assessmentAttemptReports.reduce(
    (sum, report) => sum + report.attempts.length,
    0
  );

  const highestCoins = Math.max(
    ...assessmentAttemptReports.flatMap(report =>
      report.attempts.map(attempt => attempt.coinsEarned)
    )
  );

  return {
    status: 'success',
    responseBody: {
      topicName: faker.helpers.arrayElement([
        'Introduction to AI',
        'Machine Learning Basics',
        'Deep Learning Fundamentals',
        'Neural Networks',
      ]),
      numberOfAttempts: totalAttempts,
      coinsEarnedHighest: highestCoins,
      assessmentAttemptReports,
      quizReports: [],
    },
    errorCode: 0,
    errorMessage: null,
  };
};

export const getTopicAssessmentSummaryMockHandler = http.get(
  `${env.API_URL}/assessment/v1/users/reports/get-topic-assessment-summary`,
  async ({
    request,
  }): Promise<HttpResponse<GetTopicAssessmentSummary.Root>> => {
    await networkDelay();

    const url = new URL(request.url);
    const topicId =
      url.searchParams.get('topicId') ?? 'd2135388-38f2-4d33-bdad-c684ae6d876c';

    const mockResponse = generateTopicAssessmentSummary(topicId);

    return HttpResponse.json(mockResponse);
  }
);
